import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Stack, Text } from '@99designs/emu';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { TileSelect } from '@99designs/spa-common';
import { Answer } from '../../components/Step/Answer';
import { ReactComponent as WebDevliverablesSVG } from './custom-web-deliverables.svg';
import { ReactComponent as WebBuilderBrowserSVG } from './web-builder-browser.svg';
import type { StepComponentProps, StepHeadingProps } from '../../components/Onboarder/Config';
import type { Data } from '../config';

export type DeliverableTypeValue = 'complete-website' | 'website-design';

export function DeliverableTypeStepInputs({ data, onChange }: StepComponentProps<Data>) {
    return (
        <TileSelect<DeliverableTypeValue>
            name="deliverableType"
            value={data.deliverableType}
            options={[
                {
                    label: (
                        <DeliverableContent
                            mainIcon={<WebBuilderBrowserSVG />}
                            heading={__('A finished website, ready for customers')}
                            body={__(
                                'Get online with a web builder, then our designers will bring it to life with stunning design.'
                            )}
                            data-testid={'complete-website'}
                            // eslint-disable-next-line eqeqeq
                            isSelected={data.deliverableType == 'complete-website'}
                            onChange={() =>
                                onChange(
                                    clearBranchingData({
                                        ...data,
                                        deliverableType: 'complete-website',
                                    })
                                )
                            }
                        />
                    ),
                    value: 'complete-website',
                },
                {
                    label: (
                        <DeliverableContent
                            mainIcon={<WebDevliverablesSVG />}
                            heading={__('Design files, ready for developers')}
                            body={__(
                                'Our designers will provide you with a stunning, custom web design which can be passed on to a developer of your choice. File types may include Photoshop (PSD) or Sketch design files.'
                            )}
                            data-testid={'website-design'}
                            // eslint-disable-next-line eqeqeq
                            isSelected={data.deliverableType == 'website-design'}
                            onChange={() =>
                                onChange(
                                    clearBranchingData({
                                        ...data,
                                        deliverableType: 'website-design',
                                    })
                                )
                            }
                        />
                    ),
                    value: 'website-design',
                },
            ]}
            onChange={(value, name) => {
                onChange(
                    clearBranchingData({
                        ...data,
                        [name]: value,
                    })
                );
            }}
        />
    );
}

function clearBranchingData(data: Data): Data {
    const newData = { ...data };

    if (newData.deliverableType === 'complete-website') {
        newData.workingMethod = undefined;
    }
    if (newData.deliverableType === 'website-design') {
        newData.budget = undefined;
    }

    return newData;
}

export const answerCopy = (): { [key: string]: string } => ({
    'complete-website': __('a finished website, ready for customers'),
    'website-design': __('design files, ready for developers'),
});

export const questionCopy = () => __('By the end of this process I need {answer}');

export function DeliverableTypeStepHeading({ data }: StepHeadingProps<Data>) {
    const answer = data.deliverableType ? answerCopy()[data.deliverableType] : '';

    return (
        <FormattedMessage message={questionCopy()} values={{ answer: <Answer text={answer} /> }} />
    );
}

const MainInlineSVG = styled.div`
    width: 90px;

    svg {
        display: inline-block;
        width: inherit;
        height: inherit;
    }
`;

interface DeliverableContentProps {
    heading: string;
    body: string;
    mainIcon: React.ReactNode;
    isSelected: boolean;
    onChange: () => void;
}

function DeliverableContent({ heading, body, mainIcon, ...props }: DeliverableContentProps) {
    return (
        <Box padding="2bu" data-testid={'deliverable-option'} {...props}>
            <Stack space="2bu">
                <MainInlineSVG>{mainIcon}</MainInlineSVG>
                <Stack space="0.5bu">
                    <Heading appearanceLevel={4} accessibilityLevel={3}>
                        {heading}
                    </Heading>
                    <Text as="p" color="grey700" size="size2">
                        {body}
                    </Text>
                </Stack>
            </Stack>
        </Box>
    );
}
